import { useEffect } from "react";
import { Col, Row, Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import FloatingButtons from "./FloatingButtons";
import { GiFishingPole } from "react-icons/gi";

const benefits = [
  { icon: "🎣", title: "Showcase Your Services", desc: "Create a detailed listing for your guide or charter service with photos, packages, and pricing." },
  { icon: "📅", title: "Booking Management", desc: "Manage all your bookings from a single, easy-to-use dashboard. No more back-and-forth emails." },
  { icon: "💳", title: "Secure Payments", desc: "Get paid securely through BBR's payment system. No chasing invoices." },
  { icon: "💬", title: "Direct Customer Messaging", desc: "Communicate with clients through our built-in, secure messaging platform." },
  { icon: "📱", title: "iOS & Android Apps", desc: "Run your charter business from your phone with our native mobile apps." },
  { icon: "📈", title: "Start with 10% commission. Or 5% with a referral code", desc: "Reduce your commission down to 0% with our pro staff referral system." },
  { icon: "🎯", title: "Full Business Control", desc: "Set your own prices, packages, and availability. BBR is your platform, not your boss." },
  { icon: "🤖", title: "24/7 AI Chatbot Support", desc: "Our chatbot assists your customers around the clock, answering questions even when you're on the water." },
];

export default function ListFishingCharter() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <FloatingButtons />

      {/* Hero */}
      <section className="cs-bg-dark py-5 cs-section" style={{ borderBottom: "1px solid #1e1e1e" }}>
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <p
                className="fw-bold text-uppercase mb-3"
                style={{ color: "#ddb467", letterSpacing: "4px", fontSize: "0.78rem" }}
              >
                For Fishing Guides & Charter Operators
              </p>
              <h1 className="fw-bold text-white mb-4" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", lineHeight: 1.15 }}>
                Grow Your Charter Business.{" "}
                <span style={{ color: "#ddb467" }}>On Your Terms.</span>
              </h1>
              <p className="text-secondary mb-4" style={{ fontSize: "1.05rem" }}>
                BBR Boat Rentals is a USA-based booking platform purpose-built for fishing guides, charter operators, and rental businesses. We help you attract more customers, streamline bookings, and keep more of your revenue.
              </p>
              <p className="text-secondary mb-5" style={{ fontSize: "1.05rem" }}>
                Stop losing business to high-fee platforms. BBR is built so professional operators thrive — not just survive.
              </p>
              <Link
                to="/auth/signup-owner"
                className="btn btn-lg fw-bold text-uppercase px-5 py-3"
                style={{
                  backgroundColor: "#ddb467",
                  color: "#000",
                  borderRadius: "4px",
                  letterSpacing: "1px",
                  fontSize: "0.95rem",
                }}
              >
                Free to Sign Up →
              </Link>
              <p className="text-secondary mt-3" style={{ fontSize: "0.85rem" }}>
                No subscription fees. No setup costs. Just more bookings.
              </p>
            </Col>
            <Col lg={6}>
              <img
                src="/assets/frontend/images/bbr-boat-3.jpeg"
                alt="Fishing guide on water"
                className="img-fluid rounded shadow-lg"
                style={{ width: "100%", height: "420px", objectFit: "cover" }}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5" style={{ backgroundColor: "#0d0d0d" }}>
        <Container>
          <Row className="justify-content-center text-center mb-5">
            <Col lg={7}>
              <h2 className="fw-bold text-white text-uppercase mb-3">
                How BBR Works For{" "}
                <span style={{ color: "#ddb467" }}>Charter Operators</span>
              </h2>
              <div style={{ width: "60px", height: "3px", backgroundColor: "#ddb467", margin: "0 auto" }} />
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            {[
              { step: "01", title: "Create Your Free Account", desc: "Sign up as a boat owner or charter operator. No credit card or subscription required." },
              { step: "02", title: "Build Your Service Listing", desc: "Add your charter packages, photos, pricing, and availability — all in one place." },
              { step: "03", title: "Get Booked & Get Paid", desc: "Customers discover and book your services directly. You receive secure payments instantly." },
            ].map((s) => (
              <Col key={s.step} lg={4} md={6}>
                <div
                  className="h-100 p-4 rounded"
                  style={{ backgroundColor: "#111", border: "1px solid #2a2a2a" }}
                >
                  <p className="fw-bold mb-2" style={{ color: "#ddb467", fontSize: "2rem", fontFamily: "monospace" }}>
                    {s.step}
                  </p>
                  <h5 className="text-white fw-bold mb-2">{s.title}</h5>
                  <p className="text-secondary mb-0" style={{ fontSize: "0.9rem" }}>{s.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Benefits */}
      <section className="py-5" style={{ backgroundColor: "#111" }}>
        <Container>
          <Row className="justify-content-center text-center mb-5">
            <Col lg={7}>
              <h2 className="fw-bold text-white text-uppercase mb-3">
                Built For{" "}
                <span style={{ color: "#ddb467" }}>Professional Operators</span>
              </h2>
              <p className="text-secondary">
                Every feature on BBR is designed to help fishing guide and charter businesses operate efficiently and profitably.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            {benefits.map((b, i) => (
              <Col key={i} lg={4} md={6}>
                <div
                  className="d-flex align-items-start gap-3 p-3 rounded"
                  style={{ backgroundColor: "#0d0d0d", border: "1px solid #222" }}
                >
                  <span style={{ fontSize: "1.6rem", flexShrink: 0 }}>{b.icon}</span>
                  <div>
                    <h6 className="text-white fw-bold mb-1">{b.title}</h6>
                    <p className="text-secondary mb-0" style={{ fontSize: "0.85rem" }}>{b.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why BBR vs Others */}
      <section className="py-5" style={{ backgroundColor: "#ddb467" }}>
        <Container>
          <Row className="justify-content-center text-center mb-4">
            <Col lg={8}>
              <h2 className="fw-bold text-black text-uppercase mb-2">Why Choose BBR?</h2>
              <p className="text-black">
                Free to sign up! Lowest industry commission, reduce your commission to 0% with our pro staff referrals program. No monthly subscriptions! Limited time offer for our founding members, sign up today!
              </p>
            </Col>
          </Row>
          <Row className="g-3 justify-content-center">
            {[
              "Start with 10% commission or 5% with a referral code",
              "Platform built for anglers and operators",
              "Reduce commission down to 0% through BBR's pro staff referral program",
              "A growing community of serious anglers and boating enthusiasts",
            ].map((point, i) => (
              <Col key={i} lg={6}>
                <div className="d-flex align-items-start gap-2">
                  <span className="fw-bold" style={{ color: "#000", fontSize: "1.1rem" }}>✔</span>
                  <p className="text-black mb-0 fw-medium">{point}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-5 cs-bg-dark" style={{ borderTop: "1px solid #1e1e1e" }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <GiFishingPole style={{ fontSize: "48px", color: "#ddb467", marginBottom: "16px" }} />
              <h2 className="fw-bold text-white mb-3 text-uppercase">
                Ready to Grow Your Charter Business?
              </h2>
              <p className="text-secondary mb-4" style={{ maxWidth: "520px", margin: "0 auto 24px" }}>
                Listing your fishing guide or charter service on BBR is completely free. Sign up today and start reaching more customers.
              </p>
              <Link
                to="/auth/signup-owner"
                className="btn btn-lg fw-bold text-uppercase px-5 py-3"
                style={{
                  backgroundColor: "#ddb467",
                  color: "#000",
                  borderRadius: "4px",
                  letterSpacing: "1px",
                  fontSize: "0.95rem",
                }}
              >
                Free to Sign Up →
              </Link>
              <p className="text-secondary mt-3 mb-0" style={{ fontSize: "0.85rem" }}>
                Already have an account?{" "}
                <Link to="/auth" className="fw-bold" style={{ color: "#ddb467", textDecoration: "underline" }}>
                  Log In
                </Link>
              </p>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

